import { useState } from 'react';
import { cn } from '@/lib/cn';
import { Badge } from '@/ui/Badge';
import { Button, estiloDeBoton } from '@/ui/Button';
import { Skeleton } from '@/ui/Skeleton';
import { BarraDeProgreso } from '@/components/BarraDeProgreso';
import { EstadoError } from '@/components/EstadoError';
import { EstadoVacio } from '@/components/EstadoVacio';
import { IconoDePortada, NIVELES } from '../piezas';
import { GestionDeRuta } from './GestionDeRuta';
import { tomarGestionPendiente, useRuta, type PasoDeRuta, type RutaDetalle } from './api';

/**
 * DetalleDeRuta — `#rutas/<id>`: la ruta, su avance y la escalera de pasos.
 *
 * ## Lo que se veía y no era
 *
 * **La página no se enteraba de lo que se hacía en el panel.** Añadir, quitar,
 * reordenar o marcar un curso como requerido repintaba solo el panel de gestión;
 * al cerrarlo, la página de detrás seguía enseñando la secuencia de antes hasta
 * salir y volver. Aquí los dos leen la misma consulta.
 *
 * **El candado señalaba el paso equivocado.** Decía «Se abre al terminar» el
 * paso inmediatamente anterior, aunque fuera opcional. Pero el servidor bloquea
 * por los pasos REQUERIDOS sin terminar: con un opcional en medio, el candado
 * mandaba a hacer un curso que no abría nada. Ahora nombra el requerido
 * pendiente más cercano.
 *
 * Y el estado de cada paso —completado, en progreso, bloqueado— iba solo en un
 * icono de color con su texto oculto. Ahora es una insignia con su palabra.
 */
export function DetalleDeRuta({ id }: { id: number }) {
  const puedeGestionar = Auth.can('cursos', 'manage') || Auth.isStaff();
  const ruta = useRuta(id);
  // Recién creada desde la lista: se abre la gestión para añadir cursos.
  const [gestionando, setGestionando] = useState(() => puedeGestionar && tomarGestionPendiente(id));

  if (ruta.isLoading) {
    return (
      <div className="flex flex-col gap-4">
        <Skeleton className="h-9 w-40" />
        <Skeleton className="h-64 w-full" />
        <Skeleton className="h-20 w-full" />
        <Skeleton className="h-20 w-full" />
      </div>
    );
  }

  if (ruta.isError || !ruta.data) {
    return (
      <EstadoError
        err={ruta.error}
        que="la ruta"
        onReintentar={() => void ruta.refetch()}
        onVolver={() => APP.navigate('learning-paths')}
      />
    );
  }

  const r = ruta.data;

  return (
    <>
      <div className="mb-4 flex flex-wrap items-center gap-2">
        {/* Enlace y no botón: `APP.navigate` usa `replaceState` y el Atrás del
            navegador se saltaría la lista. */}
        <a
          href="#rutas"
          className="inline-flex min-h-11 items-center gap-1.5 rounded-sm px-2 text-3 text-ink-2
                     no-underline hover:text-brand-ink focus-visible:focus-anillo"
        >
          <i className="bi bi-arrow-left" aria-hidden="true" /> Rutas
        </a>
        {r.is_mandatory && <Badge tone="danger">Obligatoria</Badge>}
        <Badge>
          <i className={cn('bi', r.sequential ? 'bi-list-ol' : 'bi-shuffle')} aria-hidden="true" />
          {r.sequential ? 'En orden' : 'En cualquier orden'}
        </Badge>
        {puedeGestionar && (
          <Button variant="secondary" size="sm" className="ml-auto" onClick={() => setGestionando(true)}>
            <i className="bi bi-pencil-square" aria-hidden="true" /> Gestionar ruta
          </Button>
        )}
      </div>

      <Cabecera r={r} puedeGestionar={puedeGestionar} />

      <section aria-labelledby="ruta-pasos">
        <h2 id="ruta-pasos" className="mb-3 flex items-center gap-2 text-4 font-bold text-brand-ink">
          <i className="bi bi-list-ol" aria-hidden="true" /> Cursos de la ruta
        </h2>
        {r.courses.length ? (
          <Escalera pasos={r.courses} />
        ) : (
          <EstadoVacio
            icono="bi-collection"
            titulo="Esta ruta todavía no tiene cursos"
            descripcion={
              puedeGestionar
                ? 'Añádelos desde la gestión: el orden en que los pongas es el orden en que se hacen.'
                : 'Cuando Bienestar le añada cursos, aparecerán aquí en orden.'
            }
            accion={puedeGestionar ? { texto: 'Añadir cursos', onClick: () => setGestionando(true) } : undefined}
            compacto
          />
        )}
      </section>

      {puedeGestionar && (
        <GestionDeRuta ruta={r} abierta={gestionando} onCerrar={() => setGestionando(false)} />
      )}
    </>
  );
}

/* ── La cabecera ───────────────────────────────────────────────────────── */

function Cabecera({ r, puedeGestionar }: { r: RutaDetalle; puedeGestionar: boolean }) {
  const pct = Math.round(r.progress_pct || 0);
  const total = r.required_count || r.course_count;
  const audiencia: Array<[string, string]> = [
    ...(r.area_name ? [['bi-diagram-3', r.area_name] as [string, string]] : []),
    ...(r.target_role_label ? [['bi-person-badge', r.target_role_label] as [string, string]] : []),
    ...(r.target_position ? [['bi-briefcase', r.target_position] as [string, string]] : []),
    ...(r.company_name ? [['bi-building', r.company_name] as [string, string]] : []),
    ...(r.target_user_count
      ? [['bi-person-check', `${r.target_user_count} ${r.target_user_count === 1 ? 'persona elegida' : 'personas elegidas'}`] as [string, string]]
      : []),
  ];

  return (
    <section className="mb-6 overflow-hidden rounded-md border border-line bg-surface">
      <div
        className="flex h-28 items-center justify-center"
        style={{ background: r.cover_icon_color || '#0D2B4E' }}
        aria-hidden="true"
      >
        <IconoDePortada valor={r.cover_icon} respaldo="bi-signpost-split" className="text-9 leading-none text-white" />
      </div>

      <div className="flex flex-col gap-3 p-4">
        <h1 className="text-6 font-bold leading-tight text-brand-ink [overflow-wrap:anywhere]">{r.title}</h1>

        {puedeGestionar && r.status !== 'publicado' && (
          <p className="flex items-center gap-2 rounded-sm border border-line bg-sunken px-3 py-2 text-2 text-ink-2">
            <i
              className={cn('bi', r.status === 'borrador' ? 'bi-pencil-square' : 'bi-archive', 'text-warn-ink')}
              aria-hidden="true"
            />
            {r.status === 'borrador'
              ? 'Ruta en borrador: el equipo todavía no la ve.'
              : 'Ruta archivada: el equipo ya no la ve. Se restaura desde «Gestionar ruta».'}
          </p>
        )}

        {r.description && (
          <p className="text-3 text-ink-2 [overflow-wrap:anywhere]">{r.description}</p>
        )}

        {audiencia.length > 0 && (
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="text-1 font-bold uppercase tracking-wide text-muted">Dirigida a</span>
            {audiencia.map(([icono, texto]) => (
              <Badge key={texto}>
                <i className={cn('bi', icono)} aria-hidden="true" /> {texto}
              </Badge>
            ))}
          </div>
        )}

        <div>
          <div className="mb-1.5 flex items-baseline justify-between gap-2 text-2 text-muted">
            <span>
              <i className="bi bi-mortarboard" aria-hidden="true" /> Progreso de la ruta
            </span>
            <strong className="tabular-nums text-ink-2">
              {r.completed_count}/{total} · {pct} %
            </strong>
          </div>
          <BarraDeProgreso pct={pct} etiqueta="Avance de la ruta" />
        </div>

        {r.completed && (
          <p className="flex items-center gap-2 rounded-sm bg-ok/12 px-3 py-2 text-3 font-semibold text-ok-ink">
            <i className="bi bi-trophy-fill" aria-hidden="true" /> Completaste esta ruta de aprendizaje.
          </p>
        )}
      </div>
    </section>
  );
}

/* ── La escalera ───────────────────────────────────────────────────────── */

/**
 * Los pasos, con el punto y la línea.
 *
 * El vanilla lo dijo mejor que nadie y se conserva: **en una rejilla los cinco
 * cursos parecen alternativas; en una escalera, pasos.** La línea se pinta
 * verde hasta donde llega el avance. El carril es decorativo —`aria-hidden`—:
 * el número y el estado de cada paso van escritos en su tarjeta.
 */
function Escalera({ pasos }: { pasos: PasoDeRuta[] }) {
  return (
    <ol className="flex flex-col">
      {pasos.map((p, i) => {
        const primero = i === 0;
        const ultimo = i === pasos.length - 1;
        return (
          <li key={p.lpc_id} className="flex gap-3.5">
            <span aria-hidden="true" className="relative flex w-7 shrink-0 justify-center">
              {/* El tramo de este paso: no asoma por encima del primero ni por
                  debajo del último, y con un solo paso no hay tramo. */}
              {!(primero && ultimo) && (
                <span
                  className={cn(
                    'absolute left-1/2 w-0.5 -translate-x-1/2',
                    p.completed ? 'bg-ok' : 'bg-line-strong',
                    primero ? 'top-[22px] bottom-0' : ultimo ? 'top-0 h-[22px]' : 'inset-y-0',
                  )}
                />
              )}
              <span
                className={cn(
                  'relative mt-2 flex size-7 items-center justify-center rounded-full border-[3px] border-surface',
                  'text-2 font-bold',
                  p.completed ? 'bg-ok text-white' : 'bg-sunken text-ink-2',
                )}
              >
                {p.completed ? (
                  <i className="bi bi-check-lg" />
                ) : p.locked ? (
                  <i className="bi bi-lock-fill" />
                ) : (
                  i + 1
                )}
              </span>
            </span>

            <Paso p={p} n={i + 1} anteriores={pasos.slice(0, i)} className={cn(!primero && 'mt-2.5')} />
          </li>
        );
      })}
    </ol>
  );
}

function Paso({
  p, n, anteriores, className,
}: {
  p: PasoDeRuta;
  n: number;
  anteriores: PasoDeRuta[];
  className?: string;
}) {
  const pct = Math.round(p.progress_pct || 0);
  const empezado = p.enrolled && pct > 0;
  // Lo que de verdad lo bloquea: el requerido sin terminar más cercano. El
  // servidor no mira los opcionales.
  const bloqueante = [...anteriores].reverse().find((q) => q.required && !q.completed);

  return (
    <div
      className={cn(
        'flex min-w-0 flex-1 flex-wrap items-center gap-3.5 rounded-md border border-line bg-surface p-3.5',
        p.locked && 'bg-sunken',
        className,
      )}
    >
      <span
        className="flex size-11 shrink-0 items-center justify-center rounded-sm"
        style={{ background: p.cover_icon_color || '#0D2B4E' }}
        aria-hidden="true"
      >
        <IconoDePortada valor={p.cover_icon} respaldo="bi-journal-bookmark" className="text-6 leading-none text-white" />
      </span>

      <div className="min-w-40 flex-1">
        <p className="flex flex-wrap items-center gap-1.5 text-1 font-bold uppercase tracking-wide text-muted">
          Paso {n}
          {!p.required && <span className="font-semibold normal-case tracking-normal">· opcional</span>}
          {p.completed ? (
            <Badge tone="ok" className="normal-case tracking-normal">
              <i className="bi bi-check-circle-fill" aria-hidden="true" /> Completado
            </Badge>
          ) : p.locked ? (
            <Badge className="normal-case tracking-normal">
              <i className="bi bi-lock-fill" aria-hidden="true" /> Bloqueado
            </Badge>
          ) : empezado ? (
            <Badge tone="warn" className="normal-case tracking-normal">
              <i className="bi bi-play-fill" aria-hidden="true" /> En progreso
            </Badge>
          ) : null}
        </p>
        <p className="truncate text-3 font-semibold text-ink" title={p.title}>{p.title}</p>
        <p className="text-2 text-muted">
          <i className="bi bi-collection-play" aria-hidden="true" /> {p.lesson_count}{' '}
          {p.lesson_count === 1 ? 'lección' : 'lecciones'}
          {p.level ? ` · ${NIVELES[p.level] ?? p.level}` : ''}
          {p.enrolled && !p.completed ? ` · ${pct} %` : ''}
        </p>
      </div>

      <div className="shrink-0">
        {p.completed ? (
          <a
            href={`#course-detail/${p.course_id}`}
            className={cn(estiloDeBoton({ variant: 'ghost', size: 'sm' }), 'no-underline')}
            aria-label={`Ver de nuevo ${p.title}`}
          >
            Ver de nuevo
          </a>
        ) : p.locked ? (
          <p className="flex max-w-64 items-start gap-1.5 text-2 text-muted">
            <i className="bi bi-lock mt-0.5" aria-hidden="true" />
            {bloqueante ? `Se abre al terminar «${bloqueante.title}»` : 'Completa el paso anterior'}
          </p>
        ) : (
          <a
            href={`#course-detail/${p.course_id}`}
            className={cn(estiloDeBoton({ variant: 'primary', size: 'sm' }), 'no-underline')}
            aria-label={`${empezado ? 'Continuar' : 'Empezar'} ${p.title}`}
          >
            {empezado ? 'Continuar' : 'Empezar'}
          </a>
        )}
      </div>
    </div>
  );
}
